import { ApiResource } from '@/core/models/api-resource.interface';
import { StoreState } from '@/core/models/store.interface';
import { LocationQuery } from 'vue-router';
import { ActionContext } from 'vuex';
import { Team } from './../teams/team.interface';
import action, { ActionState } from './actions/action.store';
import role, { RoleState } from './roles/role.store';
import team, { TeamState } from './teams/team.store';
import { User } from './user.interface';
import { UserService } from './user.service';
import { PaginatedResponse } from '@/core/models/paginated-response.interface';

const userService = new UserService();

export interface UserState {
   usersOverview?: PaginatedResponse<Array<User>>;

   user?: User;
   loadingUser: boolean;

   action?: ActionState;
   team?: TeamState;

   role?: RoleState;
}

const state: UserState = {
   loadingUser: true,
};

const getters = {
   usersOverview(state: UserState) {
      return state.usersOverview;
   },
   user(state: UserState) {
      return state.user;
   },
};

const mutations = {
   SET_USERS_OVERVIEW(state: UserState, usersOverview?: PaginatedResponse<Array<User>>) {
      state.usersOverview = usersOverview;
   },
   REMOVE_USER(state: UserState, userId: number) {
      const userIndex = (state.usersOverview?.data || [])?.findIndex((user) => user.id === userId);
      if (userIndex > -1) state.usersOverview?.data.splice(userIndex, 1);
   },
   SET_USER(state: UserState, user?: User) {
      state.user = user;
   },
   SET_LOADING_USER(state: UserState, loading: boolean) {
      state.loadingUser = loading;
   },

   ADD_USER_TEAM(state: UserState, team: Team) {
      state.user?.teams?.push(team);
   },
   REMOVE_USER_TEAM(state: UserState, teamId: number) {
      const teamIndex = (state.user?.teams || [])?.findIndex((team) => team.id === teamId);
      if (teamIndex > -1) state.user?.teams?.splice(teamIndex, 1);
   },
};

const actions = {
   async getUsers(context: ActionContext<UserState, StoreState>, query: LocationQuery): Promise<ApiResource<User>> {
      const users = await userService.getUsers(query);
      context.commit('SET_USERS_OVERVIEW', users);
      return users;
   },
   async getUser(context: ActionContext<UserState, StoreState>, payload: { userId: number; query: LocationQuery }): Promise<User> {
      context.commit('SET_LOADING_USER', true);

      return await userService
         .getUser(payload.userId, payload.query)
         .then((user) => {
            context.commit('SET_USER', user);
            return user;
         })
         .finally(() => context.commit('SET_LOADING_USER', false));
   },
   async createUser(context: ActionContext<UserState, StoreState>, payload: User): Promise<User> {
      return await userService.postUser(payload);
   },
   async changeUser(context: ActionContext<UserState, StoreState>, payload: { userId: number; user: User }): Promise<User> {
      const user = await userService.putUser(payload.userId, payload.user);
      context.commit('SET_USER', user);
      return user;
   },
   async deleteUser(context: ActionContext<UserState, StoreState>, userId: number): Promise<User> {
      return await userService.deleteUser(userId).then((response) => {
         context.commit('REMOVE_USER', userId);
         return response;
      });
   },
};

export default {
   namespaced: true,
   state,
   getters,
   mutations,
   actions,
   modules: {
      action,
      team,
      role,
   },
};
